/*
  <Card
    state                 // Top left corner badge, eg: "live", "available"
    thumbnailImg          // Thumbnail Image URL
    title                 // Card Title
    avatarImg             // Avatar Image URL
    verified              // Boolean, if the user is verified
    username              // Username or channel
    tags                  // Array of tags ["tag1", "tag2"]
    horizontal            // Horizontal Card
  />
*/
import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { SmallAvatar } from "../Avatar";
import Button from "../Button";
import Dropdown from "../Dropdown";
import ReportModal from "../modals/Report";
import Modal from "../Modal";
import { handleBigNumbers } from "../../utils";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { Tags } from "./Game";

// Import Icons
import { ReactComponent as MoreIcon } from "../../img/icons/more.svg";
import { ReactComponent as VerifiedIcon } from "../../img/icons/verified.svg";
import ReportIcon from "../../img/icons/report.svg";

export const CardSkeleton = (props) => {
  const theme = useTheme();
  return (
    <SkeletonTheme color={theme.color.secondaryBg} highlightColor={theme.color.background}>
      <div {...props}>
        <Skeleton width={275} height={170} style={{ marginBottom: "10px" }} />
        <div style={{ display: "flex" }}>
          <Skeleton circle={true} width={36} height={36} style={{ marginRight: "10px" }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Skeleton width={150} />
            <Skeleton width={100} />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
};
// Main component
const Card = (props) => {
  const [loading, setLoading] = useState(1);
  const [drop, setdrop] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const watchLink = props.eventSlug ? `/events/${props.eventSlug}/watch` : ("/stream/" + props.username);
  const cardop = [
    {
      type: 2,
      title: "Report",
      icon: ReportIcon,
      func: () => setReportModal(true),
    },
  ];

  const handleMore = () => {
    setdrop(!drop);
  };
  // props.thumbnail && props.title && props.tags && props.username && props.avatar
  return (
    <>
      <CardContainer game={props.game} horizontal={props.horizontal} {...props} loading={loading}>
        <Thumbnail
          username={props.username}
          thumbnail={props.thumbnail}
          gif={props.gif}
          title={props.title}
          state="live"
          horizontal={props.horizontal}
          setLoading={setLoading}
          to={watchLink}
        />
        <Body>
          {!props.horizontal && (
            <SmallAvatar
              style={{ marginRight: "5px" }}
              to={"/profile/" + props.username}
              src={props.avatar}
              alt={props.title}
            />
          )}
          <div style={{ flex: "1" }}>
            {props.title && (
              <Link to={watchLink}>
                <Title>{props.title}</Title>
              </Link>
            )}
            {props.gamename && props.horizontal && (
              <GameName>
                <Link to={"/game/" + props.gameid}>{props.gamename} .</Link>
                {props.views && <span> {handleBigNumbers(props.views)} views</span>}
              </GameName>
            )}
            {props.username && (
              <Link
                to={"/profile/" + props.username}
                style={{ display: "flex", alignItems: "center" }}
              >
                {props.horizontal && (
                  <SmallAvatar
                    style={{ marginRight: "5px" }}
                    src={props.avatar}
                    alt={props.title}
                  />
                )}
                {props.username && (
                  <Username>
                    {props.username}
                    {props.isVerified && <VerifiedIcon />}
                  </Username>
                )}
              </Link>
            )}
            {props.tags && (
              <Tags
                withGame={!props.horizontal}
                tags={[props.horizontal ? "" : props.gamename, ...props.tags]}
                gameid={props.gameid}
              />
            )}
          </div>
          <div style={{ position: "relative" }} onClick={handleMore}>
            <More icon className="more">
              <MoreIcon />
            </More>
            {drop && (
              <Dropdown
                show={drop}
                setShow={() => {
                  setdrop(!drop);
                }}
                style={{ top: "100%" }}
                options={cardop}
                margin="10px"
              />
            )}
          </div>
        </Body>
      </CardContainer>

      {loading === 1 && <CardSkeleton {...props} />}
      <Modal
        isOpen={reportModal}
        onRequestClose={() => setReportModal(false)}
        hideModal={() => setReportModal(false)}
      >
        <ReportModal id={props.postId} username={props.username} hideModal={setReportModal} />
      </Modal>
    </>
  );
};
export default Card;

export const Thumbnail = ({
  setLoading,
  username,
  thumbnail,
  title,
  gif,
  state,
  horizontal,
  id,
  to,
}) => {
  const fallbackThumbnail = thumbnail?.includes("placeholder.com")
    ? "/default-thumbnail.jpg"
    : thumbnail;

  const fallbackGif = gif?.includes("placeholder.com")
    ? "/default-thumbnail.gif"
    : gif;

  return (
    <ThumbnailStyle to={to || (id ? "/tournament/" + id : "/stream/" + username)} horizontal={horizontal}>
      {fallbackThumbnail && state && <Badge state={state}>{state === "live" ? "Live" : "Available"}</Badge>}
      {fallbackGif && <img className="gif" src={fallbackGif} alt={title} />}
      {fallbackThumbnail && <img src={fallbackThumbnail} alt={title} onLoad={() => setLoading(0)} />}
    </ThumbnailStyle>
  );

};

const GameName = styled.div`
  color: ${({ theme }) => theme.color.lighterText};
  font-size: 13px;
  margin-bottom: 10px;
  margin-top: 5px;
  a {
    color: ${({ theme }) => theme.color.lighterText};
    font-size: 13px;
    font-family: "Poppins Regular";
  }
`;
const CardContainer = styled.article`
  width: 275px;
  min-width: 275px;
  max-width: 275px;
  flex: 1;
  position: relative;
  display: ${({ loading }) => (loading === 1 ? "none" : "block")};
  & > a {
    display: inline-block;
    border-radius: 4px;
    width: 100%;
    margin-bottom: 10px;
    ::before {
      content: "";
      border-radius: 4px;
      overflow: hidden;
      width: calc(100% + 10px);
      height: calc(100% + 10px);
      background-color: ${({ theme }) => theme.color.secondaryBg};
      position: absolute;
      inset: -5px;
      z-index: -1;
      opacity: 0;
      visibility: hidden;
    }
    &:focus {
      ::before {
        opacity: 1;
        visibility: visible;
        transition: background-color 200ms ease;
      }
    }
  }
  &:hover {
    .more {
      opacity: 1;
      visibility: visible;
    }
  }
  ${({ horizontal }) =>
    horizontal &&
    `
      display: flex;
      max-width: 100%;
      width: 100%;
      & > a {
        max-width: 300px;
        margin-right: 20px;
        margin-bottom: 0;
      }
    `}
  @media (max-width: 768px) {
    width: 210px;
  }
`;
const Body = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  a {
    display: inline-block;
    :focus {
      h6,
      span {
        color: ${({ theme }) => theme.color.primary} !important;
      }
    }
  }
  > div:nth-of-type(1) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    > a:nth-of-type(2) {
      display: flex;
      align-items: center;
      span {
        margin-top: 0;
      }
    }
  }
`;

const Username = styled.span`
  font-size: 12px;
  font-family: "Poppins Regular";
  margin-top: 2px;
  color: ${({ theme }) => theme.color.lighterText};
  text-transform: capitalize;
  ${({ theme }) => theme.linesLimits(1, 1.3)}
  svg {
    position: relative;
    top: 1px;
    width: 20px;
  }
  :hover {
    color: ${({ theme }) => theme.color.primary};
  }
`;
export const More = styled(Button)`
  position: relative;
  left: 5px;
  opacity: 0;
  visibility: hidden;

  svg circle {
    fill: ${({ theme }) => theme.color.darkerText};
  }
`;
export const Badge = styled.div`
  user-select: none;
  pointer-events: none;
  background-color: ${({ theme, state }) =>
    state === "live" ? theme.color.danger : theme.color.primary};
  position: absolute;
  font-family: "Poppins Semibold";
  padding: 4px;
  left: 10px;
  top: 10px;
  border-radius: 4px;
  letter-spacing: 0.16em;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  color: white;
  font-size: 12px;
  z-index: 1;
`;

const Title = styled.h6`
  color: ${({ theme }) => theme.color.darkerText};
  ${({ theme }) => theme.linesLimits(1, 1.4)};
  transition: color 100ms;
  :hover {
    transition: color 200ms;
    color: ${({ theme }) => theme.color.primary};
  }
`;

export const ThumbnailStyle = styled(Link)`
  display: inline-block;
  height: 170px;
  border-radius: 4px;
  overflow: hidden;
  width: 275px;
  max-width: 275px !important;
  position: relative;
  ${({ horizontal }) =>
    horizontal &&
    `
	margin-right: 10px;
  `}

  img {
    border-radius: 4px;
    height: 100%;
    width: 100%;
    object-fit: cover;
    transition: transform 100ms;
    max-width: 275px;

    &.gif {
      position: absolute;
      inset: 0;
      opacity: 0;
      transition: opacity 100ms;
      :hover {
        opacity: 1;
        transition: opacity 200ms;
      }
    }
  }
  ::before {
    content: "";
    border-radius: 4px;
    overflow: hidden;
    width: calc(100% + 10px);
    height: calc(100% + 10px);
    background-color: ${({ theme }) => theme.color.secondaryBg};
    position: absolute;
    inset: -5px;
    z-index: -1;
    opacity: 0;
    visibility: hidden;
  }
  &:focus {
    ::before {
      opacity: 1;
      visibility: visible;
      transition: background-color 200ms ease;
    }
  }
`;
